import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  fetchAuthCapabilities,
  isLoggedIn,
  loginEmail,
  startGoogleLogin,
  startMicrosoftLogin,
  subscribe,
  type AuthCapabilities,
} from '../auth'
import { GOOGLE_LOGIN_ENABLED, MICROSOFT_LOGIN_ENABLED } from '../config'
import { MARKE_NEUTRAL } from '../lib/marke'
import { nameFuer, sichtbareAnbieter, titelFuer } from '../lib/authAnbieter'
import { useOberflaechenSprache, useT } from '../hooks/useT'
import { AgentOsZeichen } from '../components/AgentOsZeichen'
import { AnbieterZeichen } from '../components/AnbieterZeichen'
import { KundenLogo } from '../components/KundenLogo'

/**
 * Anmeldeschirm, derselbe wie im Portal (cloud-v2 `LoginScreen.tsx`, Post
 * #5421): Zeichen, Schriftzug, Unterzeile, Kundenlogo, je gemeldetem
 * Anbieter ein Knopf, darunter der Umschalter für E-Mail und Passwort.
 *
 * Die Anbieter kommen aus `GET <AUTH_API>/api/v1/auth/version`
 * (`auth_providers`; fehlt das Feld, gilt der Bestand Google). Gezeigt wird
 * ein Anbieter nur, wenn dieser Bau ihn auch starten kann: Google braucht
 * `VITE_GOOGLE_CLIENT_ID`, Microsoft die Portal-Adresse
 * `VITE_PORTAL_WEB_ORIGIN` (Weg B, Post #5420). `password_login: false`
 * blendet Umschalter und Formular aus. Antwortet die auth-api nicht, bleibt
 * es beim Passwort-Formular.
 */
export function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const t = useT()
  const sprache = useOberflaechenSprache()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showEmail, setShowEmail] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // undefined = noch nicht geladen, null = auth-api hat nicht geantwortet
  const [caps, setCaps] = useState<AuthCapabilities | null | undefined>(undefined)

  // Wohin nach dem Login: ?next=… oder Startseite. Nur relative Pfade.
  const rawNext = params.get('next') || '/'
  const dest = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/'

  // Bereits eingeloggt (oder gerade eingeloggt worden): weg von /login.
  useEffect(() => {
    if (isLoggedIn()) navigate(dest, { replace: true })
    const unsubscribe = subscribe(() => {
      if (isLoggedIn()) navigate(dest, { replace: true })
    })
    return () => { unsubscribe() }
  }, [navigate, dest])

  useEffect(() => {
    let alive = true
    fetchAuthCapabilities().then((c) => { if (alive) setCaps(c) })
    return () => { alive = false }
  }, [])

  const geladen = caps !== undefined
  const passwort = caps === undefined ? false : caps === null ? true : caps.passwordLogin
  // Gemeldete Anbieter, davon die, die dieser Bau starten kann.
  const anbieter = geladen
    ? sichtbareAnbieter(caps === null ? undefined : caps.providers).filter((id) =>
        (id === 'google' && GOOGLE_LOGIN_ENABLED) || (id === 'microsoft' && MICROSOFT_LOGIN_ENABLED))
    : []
  // Ohne Anbieter ist der Umschalter eine Sackgasse: Formular gleich offen.
  const nurEmail = geladen && anbieter.length === 0
  const keinWeg = geladen && nurEmail && !passwort

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!email || !password) return
    setBusy(true)
    try {
      await loginEmail(email.trim(), password)
      navigate(dest, { replace: true })
    } catch (err) {
      setError((err as Error).message || t('anmeldung.fehlgeschlagen'))
    } finally {
      setBusy(false)
    }
  }

  const onAnbieter = async (id: string) => {
    setError(null)
    setBusy(true)
    try {
      if (id === 'microsoft') await startMicrosoftLogin(dest)
      else if (id === 'google') startGoogleLogin()
      // Seite wechselt zum Anbieter; busy bleibt bis dahin gesetzt.
    } catch (err) {
      setError((err as Error).message || String(err))
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <AgentOsZeichen className="login-zeichen" />
        <div className="login-logo">
          {MARKE_NEUTRAL ? <>Arkturian</> : <>Agent<span>OS</span></>}
        </div>
        <div className="login-subtitle">{t('anmeldung.untertitel')}</div>
        <KundenLogo />

        {!geladen && (
          <p className="login-kein-weg" data-testid="login-loading">{t('anmeldung.vorbereiten')}</p>
        )}

        {anbieter.map((id) => (
          <button
            key={id}
            type="button"
            className={`login-oauth-btn login-oauth-${id}`}
            onClick={() => { void onAnbieter(id) }}
            disabled={busy}
            data-testid={`login-${id}`}
          >
            <AnbieterZeichen anbieter={id} />
            {sprache === 'de' ? titelFuer(id) : t('anmeldung.mit', { anbieter: nameFuer(id) })}
          </button>
        ))}

        {geladen && !nurEmail && passwort && (
          <button
            type="button"
            className="login-toggle-email"
            onClick={() => { setShowEmail((v) => !v); setError(null) }}
          >
            {showEmail ? t('anmeldung.email.verbergen') : t('anmeldung.email.zeigen')}
          </button>
        )}

        {keinWeg && (
          <p className="login-kein-weg" role="alert" data-testid="login-noway">{t('anmeldung.keinWeg')}</p>
        )}

        {(showEmail || nurEmail) && passwort && (
          <form className="login-email-form login-form" onSubmit={onSubmit}>
            <input
              type="email"
              placeholder={t('anmeldung.email.platzhalter')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              disabled={busy}
            />
            <input
              type="password"
              placeholder={t('anmeldung.passwort.platzhalter')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />
            <button type="submit" className="login-submit-btn" disabled={busy || !email || !password}>
              {busy ? t('anmeldung.laeuft') : t('anmeldung.absenden')}
            </button>
          </form>
        )}

        {error && <div className="login-error" role="alert">{error}</div>}

        {geladen && passwort && (
          <p className="login-nebenweg">
            <Link to="/signup">{t('anmeldung.konto')}</Link>
          </p>
        )}
      </div>
    </div>
  )
}
